import React, { useEffect, useMemo, useState } from "react";
import RadioCard from "../../ui/RadioCard";
import Field from "../../ui/Field";
import { ClientsFetchAPI } from "../../../../../../api/Clients";
import Maps from "../../../../../../utils/Maps";
import AlertSmall from "../../../../../Alerts/AlertSmall";
import ErrorAlert from "../../../../../Alerts/ErrorAlert";
import { resolveZoneFee } from "../../../../../../utils/CalculateDistanceFee";

const IcoArrow = ({ dir = "right" }) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    style={{ transform: dir === "left" ? "rotate(180deg)" : "none" }}
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const DEFAULT_COORDS = { lat: 8.9824, lng: -79.5199 };

const normalizeClientId = (clientData) => {
  return clientData?.id || clientData?.client_id || 0;
};

const getLogoForMap = () => {
  const iconGeneral = localStorage.getItem("iconGeneral");
  const logo = localStorage.getItem("logo");
  return iconGeneral || logo || "";
};

const getGuestClientValue = () => {
  const guestClient = localStorage.getItem("guestClient");
  return guestClient;
};

const isGuestClient = (clientData) => {
  const guestClient = getGuestClientValue();
  if (guestClient === null || guestClient === undefined || guestClient === "") {
    return !clientData?.id;
  }

  return String(guestClient) !== "0";
};

const AddressForm = ({ onSave, onCancel, clientData, deliveryRules = [], contactPhone, contactEmail }) => {
  const [step, setStep] = useState(1);

  const [typeAddress, setTypeAddress] = useState("home");
  const [nameDirection, setNameDirection] = useState("Casa");
  const [numberHouse, setNumberHouse] = useState("");
  const [phone, setPhone] = useState(contactPhone || clientData?.phone || "");
  const [email, setEmail] = useState(contactEmail || clientData?.email || "");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [coordinates, setCoordinates] = useState(DEFAULT_COORDS);
  const [hasPickedLocation, setHasPickedLocation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const mapLogo = useMemo(() => getLogoForMap(), []);

  const zoneRule = useMemo(() => deliveryRules.find(r => r.fee_type === "zone"), [deliveryRules]);
  const zoneMatch = useMemo(() => {
    if (!zoneRule || !hasPickedLocation) return undefined;
    return resolveZoneFee(zoneRule, coordinates);
  }, [zoneRule, hasPickedLocation, coordinates]);
  const isOutOfCoverage = !!zoneRule && hasPickedLocation && !zoneMatch;

  useEffect(() => {
    const existing = localStorage.getItem("addressNowLocation");
    if (!existing) return;

    try {
      const parsed = JSON.parse(existing);
      if (parsed?.lat && parsed?.lng) {
        setCoordinates({
          lat: Number(parsed.lat),
          lng: Number(parsed.lng)
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const validateStep2 = () => {
    const newErrors = {};

    if (!typeAddress.trim()) newErrors.typeAddress = "Selecciona un tipo";
    if (!nameDirection.trim()) newErrors.nameDirection = "Nombre requerido";
    if (!numberHouse.trim()) newErrors.numberHouse = "Dato requerido";
    if (!phone.trim()) newErrors.phone = "Teléfono requerido";
    if (!additionalInfo.trim()) newErrors.additionalInfo = "Referencia requerida";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      AlertSmall("Completa los campos obligatorios", "warning");
      return false;
    }

    return true;
  };

  const goNext = () => {
    if (step === 1) {
      if (!hasPickedLocation && (!coordinates.lat || !coordinates.lng)) {
        AlertSmall("Selecciona una ubicación en el mapa", "warning");
        return;
      }
      if (isOutOfCoverage) {
        AlertSmall("Tu ubicación está fuera del área de cobertura", "warning");
        return;
      }
      setStep(2);
      return;
    }

    if (step === 2) {
      handleSubmit();
    }
  };

  const goBack = () => {
    if (step === 1) {
      onCancel?.();
      return;
    }
    setStep(1);
  };

  const handleTypeChange = (value) => {
    setTypeAddress(value);

    if (value === "home") {
      setNameDirection("Casa");
    } else if (value === "work") {
      setNameDirection("Trabajo");
    } else if (value === "other" && (nameDirection === "Casa" || nameDirection === "Trabajo")) {
      setNameDirection("");
    }
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;

    const payload = {
      name: nameDirection.trim(),
      lat: Number(coordinates.lat),
      lng: Number(coordinates.lng),
      phone: phone.trim(),
      house_num: numberHouse.trim(),
      client_id: normalizeClientId(clientData),
      additional_info: additionalInfo.trim(),
      email: email.trim()
    };

    const normalizedForCheckout = {
      id: `new-${Date.now()}`,
      label_key: typeAddress,
      label:
        typeAddress === "home"
          ? "Casa"
          : typeAddress === "work"
            ? "Trabajo"
            : "Otro",
      icon:
        typeAddress === "home"
          ? "🏠"
          : typeAddress === "work"
            ? "🏢"
            : "📍",
      name: payload.name,
      house_num: payload.house_num,
      phone: payload.phone,
      additional_info: payload.additional_info,
      email: payload.email,
      lat: payload.lat,
      lng: payload.lng,
      line: `${payload.house_num}, ${payload.name}`,
      sub: payload.additional_info
    };

    const guest = isGuestClient(clientData);
    const token = undefined;

    if (guest) {
      onSave(normalizedForCheckout);
      return;
    }

    try {
      setIsSaving(true);

      const response = await ClientsFetchAPI.createAddressClient(payload, token);
      const createdAddress =
        response?.data?.data ||
        response?.data?.address ||
        response?.data ||
        normalizedForCheckout;

      onSave({
        ...normalizedForCheckout,
        ...createdAddress,
        id:
          createdAddress?.id ||
          createdAddress?._id ||
          createdAddress?.address_id ||
          normalizedForCheckout.id
      });
    } catch (error) {
      console.error(error);

      if (error?.response?.data?.error) {
        ErrorAlert("", error.response.data.error, "error");
      } else {
        ErrorAlert("", "No se pudo guardar la dirección", "error");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      {step === 1 && (
        <>
          <p className="ck-section-label" style={{ marginBottom: ".75rem" }}>
            Paso 1 · Confirma tu ubicación
          </p>

          <div className="ck-map-real">
            <Maps
              modo="puntear-geolocator"
              position={setCoordinates}
              onClickCoordenada={(coords) => {
                setCoordinates(coords);
                setHasPickedLocation(true);
              }}
              logo={mapLogo}
              zoom={17}
              width={"100%"}
              height={"420px"}
              zones={zoneRule?.zones}
            />
          </div>

          <div className="ck-map-coords">
            <span>Lat: {Number(coordinates.lat || 0).toFixed(6)}</span>
            <span>Lng: {Number(coordinates.lng || 0).toFixed(6)}</span>
          </div>

          {isOutOfCoverage && (
            <div className="ck-coverage-banner ck-coverage-banner-error">
              ⚠️ Parece que tu punto está fuera del área de cobertura.<br />
              Mueve el pin hasta tu ubicación exacta o elige otra dirección.
            </div>
          )}
          {zoneMatch && (
            <div className="ck-coverage-banner ck-coverage-banner-success">
              📍 Zona: <strong>{zoneMatch.zone_name}</strong>
            </div>
          )}

          <div className="ck-actions">
            <button className="ck-btn-back" onClick={onCancel}>
              <IcoArrow dir="left" />
              Cancelar
            </button>
            <button className="ck-btn-primary" onClick={goNext} disabled={isOutOfCoverage}>
              Siguiente
              <IcoArrow />
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <p className="ck-section-label" style={{ marginBottom: ".75rem" }}>
            Paso 2 · Dirección y contacto
          </p>

          <div className="ck-radio-grid" style={{ marginBottom: "1rem" }}>
            <RadioCard
              id="home"
              selected={typeAddress === "home"}
              onSelect={() => handleTypeChange("home")}
              icon="🏠"
              title="Casa"
              sub=""
            />
            <RadioCard
              id="work"
              selected={typeAddress === "work"}
              onSelect={() => handleTypeChange("work")}
              icon="🏢"
              title="Trabajo"
              sub=""
            />
            <RadioCard
              id="other"
              selected={typeAddress === "other"}
              onSelect={() => handleTypeChange("other")}
              icon="📍"
              title="Otro"
              sub=""
            />
          </div>

          {typeAddress === "other" && (
            <Field
              label="Nombre de la dirección"
              id="addr-name"
              value={nameDirection}
              onChange={setNameDirection}
              placeholder="Ej. Casa de mamá"
              required
              error={errors.nameDirection}
            />
          )}

          <div className="ck-row">
            <Field
              label="N° de casa / apto"
              id="addr-house"
              value={numberHouse}
              onChange={setNumberHouse}
              placeholder="Ej. Apto 204"
              required
              error={errors.numberHouse}
            />
            <Field
              label="Teléfono"
              id="addr-phone"
              value={phone}
              onChange={setPhone}
              placeholder="+507 6000-0000"
              required
              error={errors.phone}
            />
          </div>

          <Field
            label="Correo"
            id="addr-email"
            value={email}
            onChange={setEmail}
            placeholder="correo@ejemplo.com"
          />

          <Field
            label="Notas / Referencia"
            id="addr-notes"
            value={additionalInfo}
            onChange={setAdditionalInfo}
            placeholder="Ej. Portón azul, frente al parque"
            required
            error={errors.additionalInfo}
          />

          <div className="ck-actions">
            <button className="ck-btn-back" onClick={goBack}>
              <IcoArrow dir="left" />
              Atrás
            </button>
            <button
              className="ck-btn-primary"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? "Guardando..." : "Guardar dirección"}
              <IcoArrow />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AddressForm;